import React from 'react';
import { ListDisplayMode } from '@/enums';
import { Skeleton } from 'react-native-skeletons';
import { SkeletonText } from '@/components/skeleton-text';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { useLayoutContext } from '@/contexts/layout-context';
import { useOrderableItemColumnCount } from '@/components/orders/orderable-item-list';
import { useUserSettingsContext } from '@/contexts/user-context';

const RADIUS = 8;
const ITEM_HEIGHT = 80;

interface Props {
  enforceSingleColumn?: boolean;
  isModal?: boolean;
}

export const CatalogItemListLoading: React.FC<Props> = ({
  enforceSingleColumn,
  isModal = false,
}) => {
  const dimensions = useWindowDimensions();
  const { largeItemListDisplayMode } = useUserSettingsContext();
  const defaultColumnCount = useOrderableItemColumnCount();
  const { isSmallLayout } = useLayoutContext();

  const maxHeightLoadingSkeletons = Math.ceil(dimensions.height / ITEM_HEIGHT);

  if (
    !enforceSingleColumn &&
    (isSmallLayout || largeItemListDisplayMode === ListDisplayMode.Grid)
  ) {
    return (
      <>
        {[...new Array(maxHeightLoadingSkeletons)].map((_, i) => (
          <View key={i} style={styles.itemRow}>
            {[...new Array(defaultColumnCount)].map((_, i) => (
              <View key={i} style={styles.item}>
                <View style={styles.topRow}>
                  <Skeleton borderRadius={RADIUS} height={56} width={56} />
                  <View style={styles.flex}>
                    <SkeletonText height={12} />
                    <SkeletonText />
                  </View>
                </View>
                <View style={styles.bottomRow}>
                  <SkeletonText />
                  <SkeletonText width="60%" />
                </View>
              </View>
            ))}
          </View>
        ))}
      </>
    );
  }

  const leftContent = (
    <>
      <Skeleton borderRadius={RADIUS} height={56} width={56} />
      <View
        style={[styles.columnFirst, isModal ? styles.columnFirstModal : null]}
      >
        <SkeletonText height={12} />
        <SkeletonText />
        <SkeletonText width="60%" />
        <SkeletonText width="40%" />
      </View>
    </>
  );
  return [...new Array(maxHeightLoadingSkeletons)].map((_, i) => (
    <View key={i} style={[styles.base, isModal ? styles.baseModal : null]}>
      <View style={[styles.row, isModal ? styles.rowModal : null]}>
        {isModal ? (
          <View style={styles.wrapperLeft}>{leftContent}</View>
        ) : (
          leftContent
        )}
        <View style={styles.columnSecond}>
          <SkeletonText />
          <SkeletonText width="60%" />
        </View>
      </View>
    </View>
  ));
};

const styles = StyleSheet.create({
  base: {
    paddingBottom: 24,
    paddingHorizontal: 46,
  },
  baseModal: {
    paddingHorizontal: 16,
  },
  bottomRow: {
    paddingTop: 12,
    width: '80%',
  },
  columnFirst: {
    flex: 1,
    maxWidth: '30%',
  },
  columnFirstModal: {
    maxWidth: '50%',
  },
  columnSecond: {
    flex: 1,
    maxWidth: '30%',
    paddingLeft: '10%',
  },
  flex: {
    flex: 1,
  },
  item: {
    flex: 1,
    paddingBottom: 24,
    paddingHorizontal: 32,
  },
  itemRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
    marginTop: 8,
  },
  row: {
    flexDirection: 'row',
    gap: 16,
  },
  rowModal: {
    justifyContent: 'space-between',
  },
  topRow: {
    flexDirection: 'row',
    gap: 16,
    width: '80%',
  },
  wrapperLeft: {
    flex: 1,
    flexDirection: 'row',
    gap: 16,
  },
});
